import axios from "axios";
import { API_BASE_URL, endpoints } from "../utils/utils";
import { decrypt, encrypt } from "../shared/utils/crypto-utils";
import i18n from "../i18n";

const uid = () => `req:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`;

export function createApiClient({ auth, loader, notify, setAuth }) {
  const api = axios.create({
    baseURL: API_BASE_URL,
    headers: { Accept: "application/json" },
    withCredentials: true,
    withXSRFToken: true,
  });

  const csrfCookieEndpoint = process.env.REACT_APP_BACKEND_URL + endpoints.csrf;

  const decryptResponse = (response) => {
    if (response?.data?.data && response?.data?.iv) {
      try {
        const decrypted = decrypt(response.data.data, response.data.iv);
        response.data = JSON.parse(decrypted);
      } catch (err) {
        console.error("Decryption failed", err);
      }
    }
    return response;
  };

  api.interceptors.request.use(async (config) => {
    if ((config.method || "get").toLowerCase() !== "get") {
      await axios.get(csrfCookieEndpoint, { withCredentials: true });
    }
    return config;
  });

  api.interceptors.request.use((config) => {
    if (!config.headers["Authorization"]) {
      config.headers["Authorization"] = `Bearer ${auth?.accessToken}`;
    }
    return config;
  });

  api.interceptors.request.use((config) => {
    if (config.data instanceof FormData) return config;

    const method = (config.method || "get").toLowerCase();
    if (
      !config.data?.data &&
      !config.data?.iv &&
      ["post", "put", "patch"].includes(method)
    ) {
      const { encrypted, iv } = encrypt(JSON.stringify(config.data));
      config.data = { data: encrypted, iv };
    }
    return config;
  });

  api.interceptors.request.use((config) => {
    const lang = i18n.language.startsWith("fr") ? "fr" : "en";
    config.headers["Accept-Language"] = lang;
    return config;
  });

  api.interceptors.request.use(
    (config) => {
      const show = config.showLoader !== false;
      if (show) {
        const key = config.loaderKey ?? uid();
        config._loadingKey = key;
        loader.add(key);
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  api.interceptors.response.use(null, async (error) => {
    const prevRequest = error?.config;
    if (error?.response?.status === 403 && !prevRequest?.sent) {
      prevRequest.sent = true;
      return api(prevRequest);
    }
    return Promise.reject(error);
  });

  api.interceptors.response.use(
    (response) => decryptResponse(response),
    (error) => {
      if (error.response) error.response = decryptResponse(error.response);
      return Promise.reject(error);
    }
  );

  api.interceptors.response.use(
    (response) => {
      if (response.config?._loadingKey) {
        loader.remove(response.config._loadingKey);
      }
      if (response.data?.message) {
        const method = (response.config?.method || "").toLowerCase();
        const url = response.config?.url || "";
        const isSuccessResponse =
          response.status >= 200 && response.status < 300;

        const shouldShowSuccessMessage =
          isSuccessResponse &&
          ["post", "put"].includes(method) &&
          !url.includes("bookings") &&
          !url.includes("promotions");

        if (shouldShowSuccessMessage) {
          notify.success(response.data.message);
        }
      }
      return response;
    },
    (error) => {
      const key = error?.config?._loadingKey;
      if (key) loader.remove(key);

      if (error?.code === "ERR_NETWORK") {
        notify.error(error.message);
      } else if (
        error.response?.status === 422 &&
        error.response?.data?.errors
      ) {
        if (!error.config?.suppressValidationToasts) {
          Object.values(error.response.data.errors)
            .flat()
            .forEach((msg) => notify.error(msg));
        }
      } else if (error.response?.status === 429) {
        if (error.response?.data?.message) {
          notify.error(error.response.data.message);
        } else if (error.response?.data?.errors) {
          Object.values(error.response.data.errors)
            .flat()
            .forEach((msg) => notify.error(msg));
        }
      }
      return Promise.reject(error);
    }
  );

  return api;
}
